import type {
  AccountingContinuity,
  AccountingExpUpdate,
  AccountingFrame,
  AccountingMesosUpdate,
  AccountingResourceUpdate,
  GameEvent,
  GameStateSnapshot,
  LiveRateMetric,
  LiveRateSnapshot,
  MetricKey,
  RankExpPoint,
  ValidatedObservation,
} from '@/domain/types'
import type { GamePreset } from '@/presets/types'

type RateMetric = 'exp' | 'mesos' | 'hp' | 'mp'

const HOUR_MS = 3_600_000
const MAX_TRUSTED_INTERVAL_MS: Readonly<Record<RateMetric, number>> = {
  exp: 15_000,
  mesos: 15_000,
  hp: 2_000,
  mp: 2_000,
}
const EXP_RATIO_TOLERANCE = 0.018
const RESOURCE_RATIO_TOLERANCE = 0.035
const EXP_WRAP_CONFIRMATION_MS = 15_000

interface MetricClock {
  lastSourceAt: number | null
  lastActiveAt: number | null
  pendingIntervalMs: number
  trustedDurationMs: number
  updatedAt: number | null
  hasBaseline: boolean
  available: boolean
}

interface ClockObservation {
  readonly continuity: AccountingContinuity
  readonly sourceAt: number | null
}

interface ExpPoint extends RankExpPoint {
  readonly ratio: number | null
  readonly estimated: boolean
  readonly sourceAt: number
}

interface PendingExpPoint {
  readonly point: ExpPoint
  readonly levelUpConfirmed: boolean
  readonly kind: 'change' | 'wrap'
}

interface PendingMesosValue {
  readonly value: number
}

interface ResourcePoint {
  readonly current: number
  readonly max: number | null
  readonly sourceAt: number
}

interface PendingResourcePoint {
  readonly point: ResourcePoint
}

export interface TrustedMetricLedgerAggregate {
  readonly expGained: number | null
  readonly expPerHour: number | null
  readonly mesosDelta: number | null
  readonly mesosPerHour: number | null
  readonly hpConsumed: number
  readonly hpConsumedPerHour: number | null
  readonly hpRecovered: number
  readonly hpRecoveredPerHour: number | null
  readonly mpConsumed: number
  readonly mpConsumedPerHour: number | null
  readonly mpRecovered: number
  readonly mpRecoveredPerHour: number | null
}

const emptyClock = (): MetricClock => ({
  lastSourceAt: null,
  lastActiveAt: null,
  pendingIntervalMs: 0,
  trustedDurationMs: 0,
  updatedAt: null,
  hasBaseline: false,
  available: false,
})

const emptyClocks = (): Record<RateMetric, MetricClock> => ({
  exp: emptyClock(),
  mesos: emptyClock(),
  hp: emptyClock(),
  mp: emptyClock(),
})

export class TrustedMetricLedger {
  private captureGeneration = 0
  private minimumSourceAt = 0
  private suspended = false
  private clocks = emptyClocks()

  private expExactAnchor: ExpPoint | null = null
  private expLiveAnchor: ExpPoint | null = null
  private expLatestConfirmed: ExpPoint | null = null
  private expPending: PendingExpPoint | null = null
  private expGained = 0
  private expProvisionalDelta = 0
  private expExactPendingDurationMs = 0
  private expTrustedDurationMs = 0
  private hasExactExpBaseline = false

  private mesosBaseline: number | null = null
  private mesosPending: PendingMesosValue | null = null
  private mesosDelta = 0
  private hasMesosBaseline = false

  private resourceBaselines: Record<'hp' | 'mp', ResourcePoint | null> = { hp: null, mp: null }
  private resourcePending: Record<'hp' | 'mp', PendingResourcePoint | null> = { hp: null, mp: null }
  private resourceLastNumericAt: Record<'hp' | 'mp', number | null> = { hp: null, mp: null }
  private hasResourceBaseline: Record<'hp' | 'mp', boolean> = { hp: false, mp: false }
  private totals = { hpConsumed: 0, hpRecovered: 0, mpConsumed: 0, mpRecovered: 0 }

  constructor(private readonly preset: GamePreset) {}

  reset(at: number, captureGeneration = 0): void {
    this.captureGeneration = captureGeneration
    this.minimumSourceAt = at
    this.suspended = false
    this.clocks = emptyClocks()
    this.expExactAnchor = null
    this.expLiveAnchor = null
    this.expLatestConfirmed = null
    this.expPending = null
    this.expGained = 0
    this.expProvisionalDelta = 0
    this.expExactPendingDurationMs = 0
    this.expTrustedDurationMs = 0
    this.hasExactExpBaseline = false
    this.mesosBaseline = null
    this.mesosPending = null
    this.mesosDelta = 0
    this.hasMesosBaseline = false
    this.resourceBaselines = { hp: null, mp: null }
    this.resourcePending = { hp: null, mp: null }
    this.resourceLastNumericAt = { hp: null, mp: null }
    this.hasResourceBaseline = { hp: false, mp: false }
    this.totals = { hpConsumed: 0, hpRecovered: 0, mpConsumed: 0, mpRecovered: 0 }
  }

  suspend(): void {
    this.suspended = true
    this.rebaselineMetrics(false)
  }

  resume(at: number, captureGeneration: number): void {
    this.suspended = false
    this.changeGeneration(at, captureGeneration)
  }

  changeGeneration(at: number, captureGeneration: number): void {
    if (captureGeneration === this.captureGeneration) return
    this.captureGeneration = captureGeneration
    this.minimumSourceAt = at
    this.rebaselineMetrics(false)
  }

  ingest(
    state: GameStateSnapshot,
    events: readonly GameEvent[],
    observation: ValidatedObservation | undefined,
    activeAt: number,
    captureGeneration: number,
  ): AccountingFrame {
    this.changeGeneration(state.capturedAt, captureGeneration)
    if (this.suspended) return this.emptyFrame(state.capturedAt)

    const exp = this.ingestExp(state, events, observation, activeAt)
    const mesos = this.ingestMesos(state, observation, activeAt)
    const hp = this.ingestResource('hp', state, observation, activeAt)
    const mp = this.ingestResource('mp', state, observation, activeAt)
    return { capturedAt: state.capturedAt, captureGeneration: this.captureGeneration, exp, mesos, hp, mp }
  }

  aggregate(): TrustedMetricLedgerAggregate {
    return {
      expGained: this.hasExactExpBaseline ? this.expGained : null,
      expPerHour: this.rate(this.expGained, this.expTrustedDurationMs, this.hasExactExpBaseline),
      mesosDelta: this.hasMesosBaseline ? this.mesosDelta : null,
      mesosPerHour: this.rate(this.mesosDelta, this.clocks.mesos.trustedDurationMs, this.hasMesosBaseline),
      hpConsumed: this.totals.hpConsumed,
      hpConsumedPerHour: this.rate(this.totals.hpConsumed, this.clocks.hp.trustedDurationMs, this.hasResourceBaseline.hp),
      hpRecovered: this.totals.hpRecovered,
      hpRecoveredPerHour: this.rate(this.totals.hpRecovered, this.clocks.hp.trustedDurationMs, this.hasResourceBaseline.hp),
      mpConsumed: this.totals.mpConsumed,
      mpConsumedPerHour: this.rate(this.totals.mpConsumed, this.clocks.mp.trustedDurationMs, this.hasResourceBaseline.mp),
      mpRecovered: this.totals.mpRecovered,
      mpRecoveredPerHour: this.rate(this.totals.mpRecovered, this.clocks.mp.trustedDurationMs, this.hasResourceBaseline.mp),
    }
  }

  liveRates(): LiveRateSnapshot {
    const liveExpTotal = this.expGained + this.expProvisionalDelta
    const liveExpDurationMs = this.expTrustedDurationMs + this.expExactPendingDurationMs
    return {
      expPerHour: this.liveMetric(liveExpTotal, this.clocks.exp, liveExpDurationMs),
      mesosPerHour: this.liveMetric(this.mesosDelta, this.clocks.mesos),
      hpConsumedPerHour: this.liveMetric(this.totals.hpConsumed, this.clocks.hp),
      hpRecoveredPerHour: this.liveMetric(this.totals.hpRecovered, this.clocks.hp),
      mpConsumedPerHour: this.liveMetric(this.totals.mpConsumed, this.clocks.mp),
      mpRecoveredPerHour: this.liveMetric(this.totals.mpRecovered, this.clocks.mp),
    }
  }

  private ingestExp(
    state: GameStateSnapshot,
    events: readonly GameEvent[],
    observation: ValidatedObservation | undefined,
    activeAt: number,
  ): AccountingExpUpdate {
    if (observation && !observation.accepted.exp) {
      if (observation.exp.availability === 'unavailable') {
        this.markUnavailable('exp')
        this.resetExpSegment()
        return this.emptyExpUpdate('unavailable')
      }
      return this.emptyExpUpdate('pending', observation.exp.capturedAt)
    }
    const point = this.expPoint(state, observation)
    if (!point) return this.emptyExpUpdate('ignored')

    const sourceAt = observation?.exp.source === 'fused' && observation.exp.value?.ratio !== null
      ? state.capturedAt
      : point.sourceAt
    const clockObservation = this.observeClock('exp', sourceAt, activeAt)
    const comparisonAnchor = point.estimated
      ? this.expLatestConfirmed
      : (this.expExactAnchor ?? this.expLatestConfirmed)
    const confirmsDelayedWrap = clockObservation.continuity === 'ignored'
      && this.expPending?.kind === 'wrap'
      && this.hasLevelUp(events, comparisonAnchor, point)
    if (clockObservation.continuity === 'ignored' && !confirmsDelayedWrap) {
      return this.emptyExpUpdate('ignored', sourceAt)
    }
    const continuity = confirmsDelayedWrap ? 'contiguous' : clockObservation.continuity
    if (continuity === 'baseline' || continuity === 'discontinuous') {
      this.establishExpBaseline(point)
      return this.emptyExpUpdate(continuity, sourceAt, point)
    }

    const levelUpConfirmed = this.hasLevelUp(events, comparisonAnchor, point)
    const strongEvidence = levelUpConfirmed || this.hasStrongExpEvidence(point, observation)
    const deltaFromLatest = comparisonAnchor
      ? this.calculateExpDelta(comparisonAnchor, point, levelUpConfirmed)
      : null

    if (deltaFromLatest === null) {
      if (comparisonAnchor && this.isPotentialExpWrap(comparisonAnchor, point)) {
        const pendingWrap = this.expPending?.kind === 'wrap' ? this.expPending : null
        if (!pendingWrap || sourceAt - pendingWrap.point.sourceAt <= EXP_WRAP_CONFIRMATION_MS) {
          if (!pendingWrap) this.expPending = { point, levelUpConfirmed: false, kind: 'wrap' }
          return this.emptyExpUpdate('pending', sourceAt, point)
        }
      }
      this.resetClockBaseline('exp', sourceAt, activeAt)
      this.establishExpBaseline(point)
      return this.emptyExpUpdate('discontinuous', sourceAt, point)
    }

    const unchanged = deltaFromLatest === 0
    if (!strongEvidence && !unchanged) {
      const pendingConfirmed = this.expPending !== null
        && this.expCandidateProgresses(this.expPending, point, levelUpConfirmed)
      if (!pendingConfirmed) {
        this.expPending = { point, levelUpConfirmed, kind: 'change' }
        return this.emptyExpUpdate('pending', sourceAt, point)
      }
    }

    this.expPending = null
    const trustedIntervalMs = this.commitClock('exp')
    this.expExactPendingDurationMs += trustedIntervalMs
    const previousExact = this.expExactAnchor
    let exactDelta: number | null = null
    let exactIntervalMs = 0
    let exactStart: RankExpPoint | null = null
    let exactEnd: RankExpPoint | null = null

    if (!point.estimated) {
      if (previousExact) {
        const exactLevelUp = levelUpConfirmed || this.hasLevelUp(events, previousExact, point)
        const resolvedDelta = this.calculateExpDelta(previousExact, point, exactLevelUp)
        if (resolvedDelta !== null) {
          exactDelta = resolvedDelta
          exactIntervalMs = this.expExactPendingDurationMs
          exactStart = this.rankExpPoint(previousExact)
          exactEnd = this.rankExpPoint(point)
          this.expGained += resolvedDelta
          this.expTrustedDurationMs += exactIntervalMs
        }
      }
      this.expExactAnchor = point
      this.hasExactExpBaseline = true
      this.expExactPendingDurationMs = 0
      this.expProvisionalDelta = 0
      this.expLiveAnchor = point
    } else {
      const anchor = this.expExactAnchor ?? this.expLiveAnchor
      this.expProvisionalDelta = anchor
        ? this.calculateExpDelta(anchor, point, levelUpConfirmed) ?? 0
        : 0
    }
    this.expLatestConfirmed = point

    return {
      continuity: 'contiguous',
      sourceAt,
      trustedIntervalMs: exactIntervalMs,
      delta: exactDelta,
      exact: !point.estimated,
      start: exactStart,
      end: exactEnd ?? (!point.estimated ? this.rankExpPoint(point) : null),
    }
  }

  private ingestMesos(
    state: GameStateSnapshot,
    observation: ValidatedObservation | undefined,
    activeAt: number,
  ): AccountingMesosUpdate {
    if (observation && !observation.accepted.mesos) {
      if (observation.mesos.availability === 'unavailable') {
        this.markUnavailable('mesos')
        this.mesosBaseline = null
        this.mesosPending = null
        return this.emptyMesosUpdate('unavailable')
      }
      return this.emptyMesosUpdate('pending', observation.mesos.capturedAt)
    }
    if (state.mesos === null) return this.emptyMesosUpdate('ignored')
    const sourceAt = this.metricObservedAt(state, observation, 'mesos')
    if (sourceAt === null || sourceAt < this.minimumSourceAt) return this.emptyMesosUpdate('ignored', sourceAt)
    const clockObservation = this.observeClock('mesos', sourceAt, activeAt)
    if (clockObservation.continuity === 'ignored') return this.emptyMesosUpdate('ignored', sourceAt)
    if (clockObservation.continuity === 'baseline' || clockObservation.continuity === 'discontinuous' || this.mesosBaseline === null) {
      this.mesosBaseline = state.mesos
      this.mesosPending = null
      this.hasMesosBaseline = true
      return this.emptyMesosUpdate(clockObservation.continuity, sourceAt, state.mesos)
    }

    const delta = state.mesos - this.mesosBaseline
    const strongEvidence = observation === undefined
    if (delta !== 0 && !strongEvidence) {
      const confirmsPending = this.mesosPending !== null
        && this.mesosCandidateProgresses(this.mesosBaseline, this.mesosPending.value, state.mesos)
      if (!confirmsPending) {
        this.mesosPending = { value: state.mesos }
        return this.emptyMesosUpdate('pending', sourceAt, state.mesos)
      }
    }

    const startValue = this.mesosBaseline
    this.mesosBaseline = state.mesos
    this.mesosPending = null
    this.mesosDelta += delta
    const trustedIntervalMs = this.commitClock('mesos')
    return {
      continuity: 'contiguous',
      sourceAt,
      trustedIntervalMs,
      delta,
      startValue,
      endValue: state.mesos,
    }
  }

  private ingestResource(
    metric: 'hp' | 'mp',
    state: GameStateSnapshot,
    observation: ValidatedObservation | undefined,
    activeAt: number,
  ): AccountingResourceUpdate {
    if (observation && !observation.accepted[metric]) {
      if (observation[metric].availability === 'unavailable') {
        this.markUnavailable(metric)
        this.resourceBaselines[metric] = null
        this.resourcePending[metric] = null
        this.resourceLastNumericAt[metric] = null
        return this.emptyResourceUpdate('unavailable')
      }
      return this.emptyResourceUpdate('pending', observation[metric].capturedAt)
    }
    const value = state[metric]
    if (value.current === null && value.ratio === null) return this.emptyResourceUpdate('ignored')

    const observedReading = observation?.[metric]
    const observedValue = observedReading?.value
    const hasVisibleBar = observedReading !== undefined
      && (observedReading.source === 'bar' || observedReading.source === 'fused')
      && observedValue?.ratio !== null
    const timelineAt = hasVisibleBar
      ? state.capturedAt
      : this.metricObservedAt(state, observation, metric)
    if (timelineAt === null) return this.emptyResourceUpdate('ignored')
    const clockObservation = this.observeClock(metric, timelineAt, activeAt)
    if (clockObservation.continuity === 'ignored') return this.emptyResourceUpdate('ignored', timelineAt)
    if (clockObservation.continuity === 'baseline' || clockObservation.continuity === 'discontinuous') {
      this.resourceBaselines[metric] = null
      this.resourcePending[metric] = null
      this.resourceLastNumericAt[metric] = null
    }
    this.hasResourceBaseline[metric] = true

    const numericCurrent = observation ? observedValue?.current ?? null : value.current
    const numericMax = observation ? observedValue?.max ?? value.max : value.max
    const numericAt = numericCurrent === null ? null : this.metricObservedAt(state, observation, metric)
    if (numericCurrent === null || numericAt === null || numericAt < this.minimumSourceAt) {
      const trustedIntervalMs = this.commitContiguousClock(metric, clockObservation.continuity)
      return this.emptyResourceUpdate(clockObservation.continuity, timelineAt, trustedIntervalMs)
    }
    if (this.resourceLastNumericAt[metric] !== null && numericAt <= this.resourceLastNumericAt[metric]!) {
      const trustedIntervalMs = this.commitContiguousClock(metric, clockObservation.continuity)
      return this.emptyResourceUpdate(clockObservation.continuity, timelineAt, trustedIntervalMs)
    }
    this.resourceLastNumericAt[metric] = numericAt
    const point: ResourcePoint = { current: numericCurrent, max: numericMax, sourceAt: numericAt }
    const baseline = this.resourceBaselines[metric]
    if (!baseline) {
      const trustedIntervalMs = this.commitContiguousClock(metric, clockObservation.continuity)
      this.resourceBaselines[metric] = point
      this.resourcePending[metric] = null
      return this.emptyResourceUpdate(clockObservation.continuity, timelineAt, trustedIntervalMs)
    }
    if (baseline.max !== null && point.max !== null && baseline.max !== point.max) {
      this.discardClockPending(metric)
      this.resourceBaselines[metric] = point
      this.resourcePending[metric] = null
      return this.emptyResourceUpdate('discontinuous', timelineAt)
    }

    const trustedIntervalMs = this.commitContiguousClock(metric, clockObservation.continuity)

    const delta = point.current - baseline.current
    if (delta === 0) {
      this.resourcePending[metric] = null
      this.resourceBaselines[metric] = point
      return this.emptyResourceUpdate(clockObservation.continuity, timelineAt, trustedIntervalMs)
    }

    const observedSource = observedReading?.source
    const sourcesAgree = observedSource !== undefined
      && this.resourceSourcesAgree(numericCurrent, numericMax, observedValue?.ratio ?? value.ratio, observedSource)
    if (observedSource === 'fused' && !sourcesAgree) {
      this.resourcePending[metric] = null
      return this.emptyResourceUpdate('pending', timelineAt, trustedIntervalMs)
    }
    const strongEvidence = observation === undefined || sourcesAgree
    if (!strongEvidence) {
      const pending = this.resourcePending[metric]
      if (!pending || !this.resourceCandidateProgresses(baseline.current, pending.point.current, point.current)) {
        this.resourcePending[metric] = { point }
        return this.emptyResourceUpdate('pending', timelineAt, trustedIntervalMs)
      }
    }

    this.resourcePending[metric] = null
    this.resourceBaselines[metric] = point
    const consumed = delta < 0 ? Math.abs(delta) : 0
    const recovered = delta > 0 ? delta : 0
    if (metric === 'hp') {
      this.totals.hpConsumed += consumed
      this.totals.hpRecovered += recovered
    } else {
      this.totals.mpConsumed += consumed
      this.totals.mpRecovered += recovered
    }
    return { continuity: 'contiguous', sourceAt: timelineAt, trustedIntervalMs, consumed, recovered }
  }

  private observeClock(metric: RateMetric, sourceAt: number, activeAt: number): ClockObservation {
    const clock = this.clocks[metric]
    clock.available = true
    clock.updatedAt = sourceAt
    if (!clock.hasBaseline || clock.lastSourceAt === null || clock.lastActiveAt === null) {
      clock.hasBaseline = true
      clock.lastSourceAt = sourceAt
      clock.lastActiveAt = activeAt
      clock.pendingIntervalMs = 0
      return { continuity: 'baseline', sourceAt }
    }
    if (sourceAt <= clock.lastSourceAt) return { continuity: 'ignored', sourceAt }
    const sourceGapMs = sourceAt - clock.lastSourceAt
    if (sourceGapMs > MAX_TRUSTED_INTERVAL_MS[metric]) {
      clock.lastSourceAt = sourceAt
      clock.lastActiveAt = activeAt
      clock.pendingIntervalMs = 0
      return { continuity: 'discontinuous', sourceAt }
    }
    clock.pendingIntervalMs += Math.max(0, activeAt - clock.lastActiveAt)
    clock.lastSourceAt = sourceAt
    clock.lastActiveAt = activeAt
    return { continuity: 'contiguous', sourceAt }
  }

  private commitClock(metric: RateMetric): number {
    const clock = this.clocks[metric]
    const interval = Math.max(0, clock.pendingIntervalMs)
    clock.trustedDurationMs += interval
    clock.pendingIntervalMs = 0
    return interval
  }

  private commitContiguousClock(metric: RateMetric, continuity: AccountingContinuity): number {
    return continuity === 'contiguous' ? this.commitClock(metric) : 0
  }

  private discardClockPending(metric: RateMetric): void {
    this.clocks[metric].pendingIntervalMs = 0
  }

  private resetClockBaseline(metric: RateMetric, sourceAt: number, activeAt: number): void {
    const clock = this.clocks[metric]
    clock.lastSourceAt = sourceAt
    clock.lastActiveAt = activeAt
    clock.pendingIntervalMs = 0
    clock.hasBaseline = true
    clock.available = true
    clock.updatedAt = sourceAt
  }

  private markUnavailable(metric: RateMetric): void {
    const clock = this.clocks[metric]
    clock.lastSourceAt = null
    clock.lastActiveAt = null
    clock.pendingIntervalMs = 0
    clock.hasBaseline = false
    clock.available = false
  }

  private rebaselineMetrics(clearAvailability: boolean): void {
    for (const metric of Object.keys(this.clocks) as RateMetric[]) {
      const clock = this.clocks[metric]
      clock.lastSourceAt = null
      clock.lastActiveAt = null
      clock.pendingIntervalMs = 0
      clock.hasBaseline = false
      if (clearAvailability) clock.available = false
    }
    this.resetExpSegment()
    this.mesosBaseline = null
    this.mesosPending = null
    this.resourceBaselines = { hp: null, mp: null }
    this.resourcePending = { hp: null, mp: null }
    this.resourceLastNumericAt = { hp: null, mp: null }
  }

  private resetExpSegment(): void {
    this.expExactAnchor = null
    this.expLiveAnchor = null
    this.expLatestConfirmed = null
    this.expPending = null
    this.expProvisionalDelta = 0
    this.expExactPendingDurationMs = 0
  }

  private establishExpBaseline(point: ExpPoint): void {
    this.expLiveAnchor = point
    this.expLatestConfirmed = point
    this.expPending = null
    this.expProvisionalDelta = 0
    this.expExactPendingDurationMs = 0
    this.expExactAnchor = point.estimated ? null : point
    if (!point.estimated) this.hasExactExpBaseline = true
  }

  private expPoint(state: GameStateSnapshot, observation: ValidatedObservation | undefined): ExpPoint | null {
    if (state.level === null || state.exp.absolute === null || !this.validExpPoint(state.level, state.exp.absolute, state.exp.ratio)) return null
    const observedAt = this.metricObservedAt(state, observation, 'exp')
    if (observedAt === null || observedAt < this.minimumSourceAt) return null
    const rawHasAbsolute = observation?.exp.value?.absolute !== null && observation?.exp.value?.absolute !== undefined
    const exact = !state.exp.estimated && (observation === undefined || (rawHasAbsolute && observedAt >= this.minimumSourceAt))
    return {
      level: state.level,
      absolute: state.exp.absolute,
      ratio: state.exp.ratio,
      estimated: !exact,
      sourceAt: observedAt,
    }
  }

  private validExpPoint(level: number, absolute: number, ratio: number | null): boolean {
    if (!Number.isInteger(level) || level < 1 || !Number.isFinite(absolute) || absolute < 0) return false
    if (ratio !== null && (!Number.isFinite(ratio) || ratio < 0 || ratio > 1)) return false
    const required = this.preset.experienceToNext(level)
    if (required === null || required <= 0 || absolute > required) return false
    return ratio === null || Math.abs(absolute / required - ratio) <= EXP_RATIO_TOLERANCE
  }

  private calculateExpDelta(previous: ExpPoint, current: ExpPoint, levelUpConfirmed: boolean): number | null {
    if (previous.level === current.level) {
      const required = this.preset.experienceToNext(current.level)
      const delta = (previous.estimated || current.estimated) && required !== null && previous.ratio !== null && current.ratio !== null
        ? Math.round((current.ratio - previous.ratio) * required)
        : current.absolute - previous.absolute
      return delta >= 0 ? delta : null
    }
    if (!levelUpConfirmed || current.level !== previous.level + 1) return null
    const required = this.preset.experienceToNext(previous.level)
    if (required === null || previous.absolute > required) return null
    return required - previous.absolute + current.absolute
  }

  private hasStrongExpEvidence(point: ExpPoint, observation: ValidatedObservation | undefined): boolean {
    return observation === undefined
      || (!point.estimated && observation.exp.source === 'fused' && observation.exp.value?.absolute !== null)
  }

  private expCandidateProgresses(pending: PendingExpPoint, current: ExpPoint, levelUpConfirmed: boolean): boolean {
    if (pending.kind === 'wrap') return false
    return this.calculateExpDelta(pending.point, current, pending.levelUpConfirmed || levelUpConfirmed) !== null
  }

  private isPotentialExpWrap(previous: ExpPoint, current: ExpPoint): boolean {
    return previous.level === current.level && current.absolute < previous.absolute
  }

  private hasLevelUp(events: readonly GameEvent[], previous: Pick<ExpPoint, 'level'> | null, current: Pick<ExpPoint, 'level'>): boolean {
    if (!previous || current.level !== previous.level + 1) return false
    return events.some((event) => event.type === 'level-up' && event.from === previous.level && event.to === current.level)
  }

  private mesosCandidateProgresses(baseline: number, pending: number, current: number): boolean {
    return this.candidateProgresses(baseline, pending, current)
  }

  private resourceSourcesAgree(current: number, max: number | null, ratio: number | null, source: string): boolean {
    if (source !== 'fused' || max === null || max <= 0 || ratio === null) return false
    return Math.abs(current / max - ratio) <= RESOURCE_RATIO_TOLERANCE
  }

  private resourceCandidateProgresses(baseline: number, pending: number, current: number): boolean {
    return this.candidateProgresses(baseline, pending, current)
  }

  private candidateProgresses(baseline: number, pending: number, current: number): boolean {
    const pendingDirection = Math.sign(pending - baseline)
    const currentDirection = Math.sign(current - baseline)
    return pendingDirection !== 0
      && currentDirection === pendingDirection
      && Math.abs(current - baseline) >= Math.abs(pending - baseline)
  }

  private metricObservedAt(
    state: GameStateSnapshot,
    observation: ValidatedObservation | undefined,
    metric: MetricKey,
  ): number | null {
    if (observation) return observation[metric].capturedAt
    return state.observedAt?.[metric] ?? state.capturedAt
  }

  private rankExpPoint(point: Pick<ExpPoint, 'level' | 'absolute'>): RankExpPoint {
    return { level: point.level, absolute: point.absolute }
  }

  private liveMetric(total: number, clock: MetricClock, durationMs = clock.trustedDurationMs): LiveRateMetric {
    if (!clock.available) {
      return { value: null, status: 'unavailable', trustedDurationMs: durationMs, updatedAt: clock.updatedAt }
    }
    const value = durationMs > 0 ? total / durationMs * HOUR_MS : 0
    return {
      value: Number.isFinite(value) ? value : 0,
      status: 'ready',
      trustedDurationMs: durationMs,
      updatedAt: clock.updatedAt,
    }
  }

  private rate(total: number, durationMs: number, hasBaseline: boolean): number | null {
    if (!hasBaseline) return null
    const value = durationMs > 0 ? total / durationMs * HOUR_MS : 0
    return Number.isFinite(value) ? value : 0
  }

  private emptyFrame(capturedAt: number): AccountingFrame {
    return {
      capturedAt,
      captureGeneration: this.captureGeneration,
      exp: this.emptyExpUpdate('ignored'),
      mesos: this.emptyMesosUpdate('ignored'),
      hp: this.emptyResourceUpdate('ignored'),
      mp: this.emptyResourceUpdate('ignored'),
    }
  }

  private emptyExpUpdate(
    continuity: AccountingContinuity,
    sourceAt: number | null = null,
    point: ExpPoint | null = null,
  ): AccountingExpUpdate {
    return {
      continuity,
      sourceAt,
      trustedIntervalMs: 0,
      delta: null,
      exact: point ? !point.estimated : false,
      start: null,
      end: point && !point.estimated ? this.rankExpPoint(point) : null,
    }
  }

  private emptyMesosUpdate(
    continuity: AccountingContinuity,
    sourceAt: number | null = null,
    value: number | null = null,
  ): AccountingMesosUpdate {
    return { continuity, sourceAt, trustedIntervalMs: 0, delta: null, startValue: null, endValue: value }
  }

  private emptyResourceUpdate(
    continuity: AccountingContinuity,
    sourceAt: number | null = null,
    trustedIntervalMs = 0,
  ): AccountingResourceUpdate {
    return { continuity, sourceAt, trustedIntervalMs, consumed: 0, recovered: 0 }
  }
}
